/**
 * Data layer for the approvals inbox (ARCHITECTURE.md §8, §5, §9;
 * ARCHITECTURE-LAYERS.md §4.1). Promoted out of `routes/approvals.tsx`'s
 * inline "data layer" section into the standard `features/<feature>/api.ts`
 * shape — the ownership-boundary reason that page previously kept it inline
 * no longer applies once every feature rolls onto one convention (§4.1, F3).
 *
 * `hc<AppType>` fetchers + the `queryOptions` factory; mutation hooks live in
 * `./hooks`.
 */
import { queryOptions } from "@tanstack/react-query";
import {
  dropNullish,
  type ApprovalResponse,
  type ListApprovalsResponse,
} from "@richrich/contracts";
import { api, unwrap } from "../../lib/api";
import { keys } from "../../lib/queryKeys";

/** `GET /api/approvals?status=pending` — orders awaiting a human decision (§5). */
export const pendingApprovalsQuery = queryOptions({
  queryKey: keys.approvals.pending(),
  queryFn: async (): Promise<ListApprovalsResponse> =>
    unwrap<ListApprovalsResponse>(await api.approvals.$get({ query: { status: "pending" } })),
  staleTime: 5_000,
  // Keep the countdown honest while the tab is open.
  refetchInterval: 15_000,
});

/**
 * `POST /api/approvals/:id/approve` — internal `mutationFn` for
 * {@link useApprove} (`./hooks`). SAFETY (§0.6): `token` is the single-use
 * value delivered only in the push payload, never returned by this API.
 */
export async function approveOrder(id: string, token: string): Promise<ApprovalResponse> {
  return unwrap<ApprovalResponse>(
    await api.approvals[":id"].approve.$post({ param: { id }, json: { token } }),
  );
}

/** `POST /api/approvals/:id/reject` — internal `mutationFn` for {@link useReject} (`./hooks`). */
export async function rejectOrder(id: string, reason?: string): Promise<ApprovalResponse> {
  return unwrap<ApprovalResponse>(
    await api.approvals[":id"].reject.$post({
      param: { id },
      json: dropNullish({ reason }),
    }),
  );
}
